import React from 'react';
import { COPY, MACRO_LABEL } from '../copy';
import { countInRange, targetRange } from '../lib/sufficiency';
import { MACROS } from '../types';
import type { Macro, MacroAmount, MacroAmounts, SufficiencyDay } from '../types';
import { HelpBody, InfoTip, MacroMark } from './parts';

const pct = (v: number, max: number) => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;

/** Bullet bar: eaten as a fill and end line, the target range as a hatched zone. */
function MacroBullet({ amount }: { amount: MacroAmount }) {
  const { eaten, target } = amount;
  if (target == null) {
    return <div className="ns-bullet" />;
  }
  const { low, high } = targetRange(target);
  const max = Math.max(target * 1.6, high * 1.25, eaten);
  return (
    <div className="ns-bullet" aria-hidden>
      <span className="ns-zone" style={{ left: pct(low, max), width: pct(high - low, max) }} />
      <span className="ns-fill" style={{ width: pct(eaten, max) }} />
      <span className="ns-dot" style={{ left: pct(eaten, max) }} />
    </div>
  );
}

function MacroRow({ macro, amount }: { macro: Macro; amount: MacroAmount }) {
  return (
    <div className={`ns-mrow ns-mrow-${macro}`}>
      <div className="ns-mrow-h">
        <MacroMark macro={macro} size="lg" />
        <span>
          <span className="ns-mrow-n">{MACRO_LABEL[macro]}</span>
          <span className="ns-mrow-g">
            <b>{Math.round(amount.eaten)} g</b>
            {amount.target != null && ` of ${Math.round(amount.target)} g`}
          </span>
        </span>
      </div>
      <MacroBullet amount={amount} />
    </div>
  );
}

export function MacrosPanel({ macros, days, daysLogged }: { macros: MacroAmounts | null; days: SufficiencyDay[]; daysLogged: number }) {
  const inRange = macros ? countInRange(days, MACROS[0]) : 0;

  return (
    <div className="ns-panel ns-side" aria-live="polite">
      <div className="ns-side-h">
        <h4>
          Macros
          <InfoTip label="macros" title={COPY.macrosHelp.title} align="right">
            <HelpBody {...COPY.macrosHelp} />
          </InfoTip>
        </h4>
        <p>Daily average against target</p>
        {macros && (
          <div className="ns-mrow-f">
            In range on{' '}
            <b>
              {inRange} of {daysLogged}
            </b>{' '}
            days
          </div>
        )}
      </div>
      {macros ? (
        <>
          {MACROS.map((m) => (
            <MacroRow key={m} macro={m} amount={macros[m]} />
          ))}
          <div className="ns-side-f">
            <span>
              <i className="ns-z" aria-hidden />
              Target range
            </span>
            <span>
              <i className="ns-dd" aria-hidden />
              Eaten
            </span>
          </div>
        </>
      ) : (
        <p className="ns-empty">{COPY.macrosEmpty}</p>
      )}
    </div>
  );
}
